import {
  useGetEventQuery,
  useGetExhibitorPackQuery,
  useGetStructureMinimalInformations,
} from "@ac-expo/api";
import {
  ExponentStructureType,
  MinimalEventForParticipation,
  OpenExponentConversationEvent,
  OpenExponentSocialsEvent,
  OpenExponentWebsiteEvent,
} from "@ac-expo/api-types";
import {
  ActivityDetailsModal,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  DrawerTitle,
  ParticipationPanel,
  SkeletonStructureDetails,
  useActivitySelection,
  useIsMobile,
  useToast,
} from "@ac-expo/ui-components";
import {
  EventScheduleType,
  getExhibitorProfilesPackNumber,
} from "@ac-expo/utils";
import StructureDetails from "@ui/components/ui/structure/StructureDetails";
import { useExhibitorActions } from "@web-app/src/hooks/useExhibitorActions";
import { useFileUrlGetter } from "@web-app/src/shared/files";
import { noop } from "lodash";
import { ComponentProps, FC, useMemo } from "react";
import { VisuallyHidden } from "react-aria";
import environment from "../../environments/environment";
import { ActivityDetailsCartPart } from "../Activity/ActivityDetailsCartPart";
import { ActivityRegisterButton } from "../Activity/ActivityRegisterButton";

type ExhibitorDrawerUnifiedProps = {
  exhibitorId: string;
  eventId?: string;
  eventSchedules?: EventScheduleType[];
  isOpen: boolean;
  onClose: () => void;
  getAppEventClickHandler?: (
    appEvent: (
      | OpenExponentConversationEvent
      | OpenExponentWebsiteEvent
      | OpenExponentSocialsEvent
    )["name"]
  ) => () => void;
  onEventParticipationCardClick?: ComponentProps<
    typeof ParticipationPanel
  >["onEventCardClick"];
  isEmbedded?: boolean;
  withTicketing?: boolean;
  preloadedStructure?: Omit<ExponentStructureType, "participations">;
  preloadedParticipations?: MinimalEventForParticipation[];
  actionGuard?: (action: () => void, fallback?: () => void, ignoreModal?: boolean) => void;
};

const ExhibitorDrawerUnified: FC<ExhibitorDrawerUnifiedProps> = ({
  exhibitorId,
  eventId,
  isOpen,
  onClose,
  getAppEventClickHandler = () => noop,
  onEventParticipationCardClick,
  isEmbedded,
  withTicketing = false,
  preloadedStructure,
  preloadedParticipations,
  actionGuard,
}) => {
  const isMobile = useIsMobile();
  const { getFileUrl } = useFileUrlGetter();
  const { toast } = useToast();

  const { data: fetchedStructure, isLoading: isFetching } =
    useGetStructureMinimalInformations(exhibitorId, eventId, {
      enabled: !preloadedStructure,
    });

  const { data: event } = useGetEventQuery(eventId ?? "", {
    enabled: !!eventId,
  });

  const { data: exhibitorPack } = useGetExhibitorPackQuery(
    eventId,
    exhibitorId
  );
  const exhibitorPackNumber = getExhibitorProfilesPackNumber(
    exhibitorPack?.exhibitorProfilesPack
  );

  const structure = preloadedStructure ?? fetchedStructure;
  const participations =
    preloadedParticipations ?? fetchedStructure?.participations ?? [];
  const isLoading = preloadedStructure ? false : isFetching;

  const exhibitorActions = useExhibitorActions({
    structureId: exhibitorId,
    structureAdminId: structure?.adminId ?? undefined,
    isLiked: structure?.isLiked,
    getAppEventClickHandler,
    onEventParticipationCardClick,
    isEmbedded,
  });

  const { closeActivityList, selectedActivities, openActivityList } =
    useActivitySelection();

  const activities = useMemo(() => {
    return (
      structure?.activities.filter((sa) =>
        selectedActivities.some((ea) => ea.activityId === sa.id)
      ) ?? []
    );
  }, [selectedActivities, structure?.activities]);

  const getActivityCartPart = (activityId: string) => {
    const activity = structure?.activities.find((a) => a.id === activityId);

    if (!activity || !withTicketing) {
      return null;
    }

    return (
      <ActivityDetailsCartPart
        activity={activity}
        close={() => closeActivityList()}
      />
    );
  };

  const getRegisterButton = (activityId: string) => {
    const activity = structure?.activities.find((a) => a.id === activityId);

    if (!activity || !withTicketing) {
      return null;
    }

    return <ActivityRegisterButton activity={activity} />;
  };

  if (!isLoading && !structure) {
    return null;
  }

  const onOpenChange = (value: boolean) => {
    if (!value) {
      onClose();
    }
  };

  const structureDetailsContent = !isStructureLoading(structure, isLoading) ? (
    <StructureDetails
      customAppearance={structure.customStructureAppearance}
      structure={structure ?? null}
      structureImageUrl={getFileUrl(structure?.picture?.key, "media")}
      onWebsiteClick={exhibitorActions.onWebsiteClick}
      onSocialsClick={exhibitorActions.onSocialsClick}
      onEventCardClick={exhibitorActions.openEventDetails}
      onConversationClick={exhibitorActions.onConversationClick}
      onOpenActivityList={openActivityList}
      toggleLike={exhibitorActions.toggleLike}
      eventSchedules={event?.dates?.schedules}
      onBackClick={onClose}
      isLiked={structure.isLiked}
      participations={participations ?? []}
      getFileUrl={getFileUrl}
      isEmbedded={isEmbedded}
      exhibitorPackNumber={eventId ? exhibitorPackNumber : undefined}
      actionGuard={actionGuard}
      onShareClick={
        eventId !== undefined || structure?.slug !== undefined
          ? () => {
              let shareUrl: string;

              if (eventId && event?.slug) {
                shareUrl = `${environment.NEXT_BASE_URL}/event/${event.slug}?panel=exponents&exhibitorId=${exhibitorId}`;
              } else {
                shareUrl = `${environment.NEXT_BASE_URL}/exponent/share/${structure?.slug}`;
              }

              navigator.clipboard.writeText(shareUrl).then(() => {
                toast({
                  title: "Lien copié dans le presse-papier",
                  description:
                    "Vous pouvez maintenant partager cette fiche exposant",
                });
              });
            }
          : undefined
      }
    />
  ) : (
    <SkeletonStructureDetails />
  );

  const titleText = !isStructureLoading(structure, isLoading)
    ? `Détails de ${structure.businessName}`
    : "Détails d'un exposant";

  return (
    <>
      {isMobile ? (
        <Drawer open={isOpen} onOpenChange={onOpenChange}>
          <DrawerOverlay />
          <VisuallyHidden>
            <DrawerTitle>{titleText}</DrawerTitle>
          </VisuallyHidden>
          <DrawerContent className="h-full bg-white p-0 rounded-none border-0">
            {structureDetailsContent}
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
          <VisuallyHidden>
            <DialogTitle>{titleText}</DialogTitle>
          </VisuallyHidden>
          <DialogContent
            className="max-w-4xl max-h-[90vh] overflow-y-auto p-0"
            withCloseButton={false}
          >
            {structureDetailsContent}
          </DialogContent>
        </Dialog>
      )}
      {activities.length > 0 && withTicketing ? (
        <ActivityDetailsModal
          open={activities.length > 0}
          setOpen={(open) => !open && closeActivityList()}
          selectedActivities={selectedActivities}
          activities={structure?.activities ?? []}
          getActivityCartPart={getActivityCartPart}
          getRegisterButton={getRegisterButton}
          onClose={closeActivityList}
        />
      ) : null}
    </>
  );
};

const isStructureLoading = (
  structure:
    | ExhibitorDrawerUnifiedProps["preloadedStructure"]
    | undefined
    | null,
  isLoading: boolean
): structure is undefined | null => isLoading || !structure;

export default ExhibitorDrawerUnified;
